import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { parseDate, formatRoleName } from 'utils'
import { showAlert } from 'services/alerts.service'
import { makeRequest } from 'services/request.service'
import TableComponent from 'components/common/TableComponent'
import ActionCardComponent from 'components/common/ActionCardComponent'
import ModalComponent from 'components/common/ModalComponent'
import PageLoadingState from 'components/common/PageLoadingState'

// Per-role user columns. The owner admin is shown in its own column; usersCount
// still includes every user of the farm.
const TENANT_ROLES = ['personnelManager', 'operationsManager', 'agronomist']

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('secret')}`
})

const fullName = (firstName, lastName) => [firstName, lastName].filter(Boolean).join(' ')

const Tenants = () => {
  const { t } = useTranslation()
  const [state, setState] = useState({
    loading: true,
    tenants: [],
    latestAppVersion: null,
    search: '',
    selectedTenant: null,
    loadingTenant: false
  })

  useEffect(() => {
    const getTenants = async () => {
      try {
        const response = await makeRequest({ method: 'GET', url: '/system/tenants', headers: getAuthHeaders() })
        if (response.status === 200) {
          setState(s => ({
            ...s,
            tenants: response.data.tenants || [],
            latestAppVersion: response.data.latestAppVersion
          }))
        }
      } catch (error) {
        console.error('Error fetching tenants:', error)
        showAlert({ message: t('system.tenants.fetchError'), icon: 'error' })
      } finally {
        setState(s => ({ ...s, loading: false }))
      }
    }
    getTenants()
  }, [])

  const openTenant = async tenant => {
    setState(s => ({ ...s, loadingTenant: true, selectedTenant: { ...tenant, users: [], devices: [] } }))
    try {
      const response = await makeRequest({ method: 'GET', url: `/system/tenants/${tenant.farmId}`, headers: getAuthHeaders() })
      if (response.status === 200) {
        setState(s => ({ ...s, selectedTenant: response.data.tenant }))
      }
    } catch (error) {
      console.error('Error fetching tenant:', error)
      showAlert({ message: t('system.tenants.fetchTenantError'), icon: 'error' })
    } finally {
      setState(s => ({ ...s, loadingTenant: false }))
    }
  }

  const closeTenant = () => setState(s => ({ ...s, selectedTenant: null }))

  if (state.loading) {
    return <PageLoadingState message={t('system.tenants.loading')} rows={6} />
  }

  const { latestAppVersion, selectedTenant } = state
  const search = state.search.toLowerCase()

  const rows = state.tenants
    .map(tenant => ({
      ...tenant,
      adminName: fullName(tenant.adminFirstName, tenant.adminLastName),
      ...Object.fromEntries(TENANT_ROLES.map(role => [role, tenant.usersByRole?.[role] || 0]))
    }))
    .filter(tenant => !search ||
      [tenant.farmName, tenant.adminName, tenant.adminEmail].some(value => String(value || '').toLowerCase().includes(search)))

  const tenantsTableData = {
    columns: [
      { key: 'farmName', labelKey: 'system.tenants.cols.farm' },
      {
        key: 'adminName',
        labelKey: 'system.tenants.cols.admin',
        cell: (value, row) => (
          <div className='flex flex-col'>
            <span>{value || '-'}</span>
            <span className='text-xs text-gray-500'>{row.adminEmail}</span>
          </div>
        )
      },
      ...TENANT_ROLES.map(role => ({ key: role, label: formatRoleName(role) })),
      { key: 'usersCount', labelKey: 'system.tenants.cols.users' },
      {
        key: 'devicesCount',
        labelKey: 'system.tenants.cols.devices',
        cell: (value, row) => latestAppVersion
          ? (
            <span className={row.devicesOnLatest < value ? 'text-amber-600 font-medium' : ''}>
              {t('system.tenants.onLatest', { onLatest: row.devicesOnLatest, total: value })}
            </span>
            )
          : value
      },
      { key: 'createdAt', labelKey: 'system.tenants.cols.createdAt', cell: value => parseDate(value, false) }
    ],
    rows
  }

  const usersTableData = {
    columns: [
      { key: 'email', labelKey: 'system.tenants.cols.email' },
      { key: 'firstName', labelKey: 'system.tenants.cols.name', cell: (value, row) => fullName(value, row.lastName) },
      { key: 'roleName', labelKey: 'system.tenants.cols.role', cell: value => formatRoleName(value || '') },
      { key: 'createdAt', labelKey: 'system.tenants.cols.createdAt', cell: value => parseDate(value, false) }
    ],
    rows: selectedTenant?.users || []
  }

  const devicesTableData = {
    columns: [
      { key: 'identifier', labelKey: 'system.tenants.cols.identifier' },
      { key: 'groupName', labelKey: 'system.tenants.cols.group', cell: value => value || '-' },
      {
        key: 'appVersion',
        labelKey: 'system.tenants.cols.appVersion',
        cell: value => (
          <span className={latestAppVersion && value !== latestAppVersion ? 'text-amber-600 font-medium' : ''}>
            {value || '-'}
          </span>
        )
      },
      { key: 'lastSync', labelKey: 'system.tenants.cols.lastSync', cell: value => parseDate(value) }
    ],
    rows: selectedTenant?.devices || []
  }

  return (
    <>
      <ActionCardComponent>
        <div data-align='left' className='flex flex-col'>
          <h3 className='text-lg font-semibold text-greenPrimary'>{t('system.tenants.title')}</h3>
          <span className='text-sm text-gray-500'>
            {t('system.tenants.summary', { count: state.tenants.length })}
            {latestAppVersion ? ` · ${t('system.tenants.latestVersion', { version: latestAppVersion })}` : ''}
          </span>
        </div>
        <input
          type='text'
          placeholder={t('system.tenants.search')}
          value={state.search}
          onChange={e => setState(s => ({ ...s, search: e.target.value }))}
          className='py-2 px-3 border rounded-md shadow-sm'
        />
      </ActionCardComponent>

      <TableComponent
        tableData={tenantsTableData}
        defaultSortColumn='farmName'
        onRowClick={openTenant}
        exportFileName='tenants'
      />

      <ModalComponent
        isOpen={!!selectedTenant}
        onClose={closeTenant}
        title={selectedTenant?.farmName || ''}
        maxWidth='5xl'
        hideDefaultFooter
      >
        {state.loadingTenant
          ? <PageLoadingState message={t('system.tenants.loading')} rows={4} />
          : (
            <div className='flex flex-col gap-6'>
              <div className='text-sm text-gray-700'>
                <span className='font-semibold'>{t('system.tenants.cols.admin')}:</span>{' '}
                {fullName(selectedTenant?.adminFirstName, selectedTenant?.adminLastName) || '-'}
                {selectedTenant?.adminEmail ? ` (${selectedTenant.adminEmail})` : ''}
              </div>
              <div>
                <h4 className='font-semibold text-greenPrimary mb-2'>
                  {t('system.tenants.usersTitle', { count: usersTableData.rows.length })}
                </h4>
                <TableComponent tableData={usersTableData} defaultSortColumn='roleName' exportable={false} />
              </div>
              <div>
                <h4 className='font-semibold text-greenPrimary mb-2'>
                  {t('system.tenants.devicesTitle', { count: devicesTableData.rows.length })}
                </h4>
                <TableComponent tableData={devicesTableData} defaultSortColumn='identifier' exportable={false} />
              </div>
            </div>
            )}
      </ModalComponent>
    </>
  )
}

export default Tenants
